import React from "react";
import { Head } from "vite-react-ssg";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export type ogTypes = "default" | "blog" | "usecase" | "news";

interface SeoProps {
  description?: string;
  lang?: string;
  meta?: Array<{ name: string; content: string; property?: string }>;
  title?: string;
  blog?: boolean;
  useCase?: boolean;
  ogImage?: ogTypes;
  /** absolute URL to declare canonical instead of this page — for moved pages */
  canonical?: string;
}

/**
 * Canonical and og:url were both hardcoded to the bare origin, so every page
 * declared itself a duplicate of the homepage. Derive the real path instead,
 * with a trailing slash only on the root so the value matches how the pages
 * are actually served.
 */
const pageUrl = (siteUrl: string, pathname: string) => {
  const clean = (pathname || "/").split("?")[0].split("#")[0].replace(/\/+$/, "");
  return clean === "" ? `${siteUrl}/` : `${siteUrl}${clean}`;
};

const siteMetadata = {
  author: "Victor Dibia",
  siteUrl: "https://multiagentbook.com",
  twitterUsername: "@designingwithml",
  keywords: "multi-agent systems, system design, agent architecture, distributed AI, design patterns, multi-agent patterns, agentic ai, agent orchestration, ai systems architecture"
};

function SEO({
  description = "",
  lang,
  meta = [],
  title,
  blog = false,
  useCase = false,
  ogImage,
  canonical,
}: SeoProps) {
  const location = useLocation();

  const { t, i18n } = useTranslation("common");
  const currentLang = lang || i18n.language || "en";

  const defaultTitle = t("seo.defaultTitle", "Designing Multi-Agent Systems");
  const defaultDescription = t("seo.defaultDescription", "Master the principles, patterns, and implementation of multi-agent systems");

  const {
    siteUrl,
    twitterUsername,
    author,
    keywords,
  } = siteMetadata;

  const getImageSrc = () => {
    // First priority: Explicit selection via ogImage
    if (ogImage === "blog") {
      return "/images/og-card.png";
    }
    if (ogImage === "usecase") {
      return "/images/og-card-usecases.png";
    }
    if (ogImage === "news") {
      return "/images/og-card-news.png";
    }
    if (ogImage === "default") {
      return "/images/og-card.png";
    }

    // Second priority: Based on page type (backwards compatibility)
    if (blog) {
      return "/images/og-card.png";
    }
    if (useCase) {
      return "/images/og-card-usecases.png";
    }

    // Default fallback
    return "/images/og-card.png";
  };

  const seo = {
    title: title || defaultTitle,
    titleTemplate: title
      ? `${title} | ${defaultTitle}`
      : `${defaultTitle} | ${defaultTitle}`,
    description: description || `${title} | ${defaultDescription}`,
    image: `${siteUrl}${getImageSrc()}`,
    url: canonical || pageUrl(siteUrl, location.pathname),
  };

  const metaTags = [
    {
      name: `description`,
      content: seo.description,
    },
    {
      name: `author`,
      content: author,
    },
    {
      name: `keywords`,
      content: keywords,
    },
    {
      name: `image`,
      content: seo.image,
    },
    {
      property: `og:title`,
      content: seo.title,
    },
    {
      property: `og:url`,
      content: seo.url,
    },
    {
      property: `og:description`,
      content: seo.description,
    },
    {
      property: `og:image`,
      content: seo.image,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:image`,
      content: seo.image,
    },
    {
      name: `twitter:creator`,
      content: twitterUsername,
    },
    {
      name: `twitter:title`,
      content: seo.title,
    },
    {
      name: `twitter:description`,
      content: seo.description,
    },
    ...meta,
  ];

  return (
    /* vite-react-ssg's Head, not react-helmet-async: this is what writes the
       title and meta into the prerendered HTML. Helmet only applies them in
       the browser, which is invisible to a crawler that does not run JS. */
    <Head>
      <html lang={currentLang} />
      <title>{seo.titleTemplate ? seo.titleTemplate.replace("%s", seo.title) : seo.title}</title>
      {metaTags.map((m: any) => (
        <meta key={m.name || m.property} {...m} />
      ))}
      <link rel="canonical" href={seo.url} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Website",
          url: seo.url,
          name: seo.title,
        })}
      </script>
    </Head>
  );
}

export default SEO;
